import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyEarningsCalendar } from "@/lib/engagement.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/bartender/earnings")({
  component: EarningsPage,
  head: () => ({
    meta: [
      { title: "My earnings · The Hospitality Reserve" },
      { name: "description", content: "Track your earnings, streaks and best weeks." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => <div className="p-10 text-center">Could not load earnings.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

function fmtGBP(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

function EarningsPage() {
  const fetchCal = useServerFn(getMyEarningsCalendar);
  const { data, isLoading } = useQuery({
    queryKey: ["my-earnings", 90],
    queryFn: () => fetchCal({ data: { days: 90 } }),
  });

  // Build a 90-day grid: weeks as columns, weekdays as rows (GitHub style).
  const today = new Date();
  const cells: Array<{ date: string; pence: number }> = [];
  const map = new Map((data?.days ?? []).map((d) => [d.date, d.pence]));
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    cells.push({ date: iso, pence: map.get(iso) ?? 0 });
  }
  const max = Math.max(1, ...cells.map((c) => c.pence));
  const intensity = (p: number) => (p === 0 ? 0 : Math.min(4, Math.ceil((p / max) * 4)));
  const SHADES = [
    "bg-muted/40",
    "bg-accent/20",
    "bg-accent/40",
    "bg-accent/60",
    "bg-accent",
  ];

  // Group last-7-days totals for week summary
  const last7 = cells.slice(-7).reduce((s, c) => s + c.pence, 0);
  const prev7 = cells.slice(-14, -7).reduce((s, c) => s + c.pence, 0);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <Link to="/bartender" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent">← Back</Link>
        <p className="mt-6 tracking-wide-luxury text-accent">Earnings</p>
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="font-display text-3xl">Your last 90 days</h1>
          <Link
            to="/bartender/annual-statement"
            className="text-xs tracking-wide-luxury text-accent underline-offset-4 hover:underline"
          >
            Annual earnings statement →
          </Link>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Stat label="Total (90d)" value={fmtGBP(data?.total_pence ?? 0)} />
          <Stat label="This week" value={fmtGBP(last7)} delta={prev7 ? Math.round(((last7 - prev7) / prev7) * 100) : null} />
          <Stat label="Best day" value={data?.best_day ? fmtGBP(data.best_day.pence) : "—"} sub={data?.best_day?.date} />
        </div>

        <div className="mt-8 rounded-md border border-border bg-card p-6">
          <p className="tracking-wide-luxury text-[0.65rem] text-muted-foreground uppercase">Heatmap</p>
          {isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <div className="grid grid-flow-col grid-rows-7 gap-1" style={{ width: "max-content" }}>
                {cells.map((c) => (
                  <div
                    key={c.date}
                    title={`${c.date} · ${fmtGBP(c.pence)}`}
                    className={`h-3.5 w-3.5 rounded-sm ${SHADES[intensity(c.pence)]}`}
                  />
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 text-[0.65rem] text-muted-foreground">
                <span>Less</span>
                {SHADES.map((s, i) => <div key={i} className={`h-3 w-3 rounded-sm ${s}`} />)}
                <span>More</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, sub, delta }: { label: string; value: string; sub?: string; delta?: number | null }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <p className="tracking-wide-luxury text-[0.6rem] text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 font-display text-2xl">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      {delta != null && (
        <p className={`mt-0.5 text-xs ${delta >= 0 ? "text-emerald-400" : "text-destructive"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}% vs last week
        </p>
      )}
    </div>
  );
}
